import { useEffect, useState } from "react";
// Lightweight currency selection + conversion (USD base). Approximate static rates for display.
const RATES = { USD: 1, AED: 3.67, ARS: 900, AUD: 1.52, AZN: 1.70, BDT: 110, INR: 83, EUR: 0.92, GBP: 0.79, CAD: 1.36, JPY: 157, SGD: 1.34, CHF: 0.88, CNY: 7.24, HKD: 7.80, NZD: 1.64, ZAR: 18.5, BRL: 5.45, MXN: 17.1, RUB: 92, TRY: 34, NGN: 1500, SAR: 3.75, KRW: 1380, IDR: 16200, MYR: 4.45, PHP: 58, THB: 34.5, VND: 25400, PKR: 278, PLN: 3.95, SEK: 10.6, NOK: 10.7, DKK: 6.85 };
const SYMBOLS = { USD: "$", AED: "د.إ", ARS: "ARS$", AUD: "A$", AZN: "₼", BDT: "৳", INR: "₹", EUR: "€", GBP: "£", CAD: "C$", JPY: "¥", SGD: "S$", CHF: "CHF", CNY: "¥", HKD: "HK$", NZD: "NZ$", ZAR: "R", BRL: "R$", MXN: "MX$", RUB: "₽", TRY: "₺", NGN: "₦", SAR: "﷼", KRW: "₩", IDR: "Rp", MYR: "RM", PHP: "₱", THB: "฿", VND: "₫", PKR: "₨", PLN: "zł", SEK: "kr", NOK: "kr", DKK: "kr" };

export const CURRENCIES = Object.keys(RATES);
export const getCcy = () => localStorage.getItem("imperio_ccy") || "USD-$";

// Map an ISO currency code (from IP geolocation) to our stored "CODE-symbol" value.
export const ccyString = (code) => (RATES[code] ? `${code}-${SYMBOLS[code]}` : "USD-$");

// One-time, location-based default. Only runs if the user hasn't chosen a currency yet.
export const initCcy = async () => {
  loadRates();
  if (localStorage.getItem("imperio_ccy")) return; // respect manual choice
  try {
    const res = await fetch("https://ipapi.co/json/");
    if (!res.ok) return;
    const data = await res.json();
    const code = (data.currency || "").toUpperCase();
    if (RATES[code]) applyCcy(ccyString(code));
  } catch (e) {
    /* offline / blocked — silently keep USD default */
  }
};

export const ccyCode = (raw) => (raw || getCcy()).split("-")[0];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const applyCcy = (val) => {
  localStorage.setItem("imperio_ccy", val);
  window.dispatchEvent(new CustomEvent("imperio-ccy", { detail: val }));
};

// Manual pick by the user: persist locally and on the account (overrides IP-based default).
export const setCcy = (val) => {
  applyCcy(val);
  localStorage.setItem("imperio_ccy_manual", "1");
  const tk = localStorage.getItem("imperio_token");
  if (tk) {
    fetch(`${API}/user/currency`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ currency: ccyCode(val) }) }).catch(() => {});
  }
};

// Live FX rates from the backend (falls back to the static table above).
export const loadRates = async () => {
  try {
    const d = await (await fetch(`${API}/fx/rates`)).json();
    Object.entries(d.rates || {}).forEach(([k, v]) => { if (v > 0) RATES[k] = v; });
    window.dispatchEvent(new CustomEvent("imperio-ccy", { detail: getCcy() }));
  } catch (e) { /* keep static rates */ }
};

// Signed-in users: the account currency (server IP-detected on login, or their saved choice) wins.
export const syncCcyFromUser = (user) => {
  const code = user?.display_currency;
  if (!code || !RATES[code]) return;
  if (!user.currency_manual) localStorage.removeItem("imperio_ccy_manual");
  if (ccyCode(getCcy()) !== code) applyCcy(ccyString(code));
};

export const convertFromUsd = (usd, raw) => {
  const code = ccyCode(raw);
  return (usd || 0) * (RATES[code] || 1);
};

export const ccySymbol = (raw) => {
  const code = ccyCode(raw);
  return SYMBOLS[code] || "$";
};

export const formatCcy = (usd, raw, digits = 2) => {
  const code = ccyCode(raw);
  const val = convertFromUsd(usd, raw);
  return `${SYMBOLS[code] || "$"}${new Intl.NumberFormat("en-US", { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits }).format(val)}`;
};

export const ccyLabel = (raw) => ccyCode(raw);

// Re-renders on currency change; returns a USD -> display-currency formatter.
export const useCcy = () => {
  const [raw, setRaw] = useState(getCcy());
  useEffect(() => {
    const on = (e) => setRaw(e.detail || getCcy());
    window.addEventListener("imperio-ccy", on);
    return () => window.removeEventListener("imperio-ccy", on);
  }, []);
  return { ccy: raw, code: ccyCode(raw), fmt: (usd) => formatCcy(usd, raw) };
};
